import { Trans, useLingui } from "@lingui/react/macro";
import { Link, useNavigate } from "@tanstack/react-router";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHeroLookup } from "../../api/useHeroLookup";
import { useMatch } from "../../api/useMatch";
import { useRankLookup } from "../../api/useRankLookup";
import { useStartAnalysis } from "../../api/useStartAnalysis";
import { formatDuration } from "../../lib/formatDuration";
import { isNormalGame } from "../../lib/isNormalGame";
import { HeroAvatar } from "../HeroAvatar";
import { AnalysisLinkLabel } from "../MatchList/AnalysisLinkLabel";
import { GameModeLabel } from "../MatchList/GameModeLabel";
import { NoAnalysisLabel } from "../MatchList/NoAnalysisLabel";
import { StartAnalysisError } from "../MatchList/StartAnalysisError";
import { MatchPlaceBadge } from "../MatchPlaceBadge";
import { MatchScoreBadge } from "../MatchScoreBadge";

type MatchCardProps = {
  matchId: number;
};

// Короткая карточка матча для всплывающих окон (точка на графике рангов): герой, исход,
// дата, K/D/A, длительность, быстрая оценка и ранг после матча. Внизу — «Открыть разбор»,
// если разбор есть, иначе «Разобрать»: запуск переходит на страницу разбора. Street Brawl
// и другие режимы — с меткой режима и без «Разобрать».
export const MatchCard = ({ matchId }: MatchCardProps) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const match = useMatch(matchId);
  const heroLookup = useHeroLookup();
  const rankLookup = useRankLookup();
  const startAnalysis = useStartAnalysis({
    onStarted: (analysis) =>
      navigate({ to: "/matches/$matchId", params: { matchId: analysis.match_id } }),
  });

  if (match.isPending) {
    return <Skeleton data-state="loading" className="h-[148px] w-full rounded-lg" />;
  }
  if (match.isError) {
    return (
      <p className="text-sm text-muted">
        <Trans>Could not load the match.</Trans>
      </p>
    );
  }
  const data = match.data;
  const hero = heroLookup(data.hero_id);
  const { kills, deaths, assists } = data;
  const startedAt = new Intl.DateTimeFormat(i18n.locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(data.started_at));
  const rank = data.rank_badge === null ? null : rankLookup(data.rank_badge).name;

  return (
    <div data-match-id={matchId} className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={44} />
        <span className="flex min-w-0 grow flex-col leading-tight">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-semibold">{hero.name}</span>
            <MatchPlaceBadge rank={data.mvp_rank} className="shrink-0" />
          </span>
          <span className="truncate text-[13px]">
            <span className={cn("font-semibold", data.won ? "text-good" : "text-bad")}>
              {data.won ? <Trans>Victory</Trans> : <Trans>Defeat</Trans>}
            </span>{" "}
            <time dateTime={data.started_at} className="text-subtle">
              · {startedAt}
            </time>
            {data.game_mode !== null && data.game_mode !== "normal" && (
              <span className="text-warn">
                {" · "}
                <GameModeLabel mode={data.game_mode} />
              </span>
            )}
          </span>
        </span>
        <MatchScoreBadge score={data.score} compact />
      </div>
      <dl className="grid grid-cols-[1.3fr_1fr_1fr] gap-2 rounded-lg bg-surface-raised px-3 py-2 text-center whitespace-nowrap">
        <div className="flex flex-col">
          <dt className="text-[11px] text-subtle">
            <Trans>K / D / A</Trans>
          </dt>
          <dd className="font-mono text-sm font-semibold">
            {kills} / {deaths} / {assists}
          </dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-[11px] text-subtle">
            <Trans>Length</Trans>
          </dt>
          <dd className="font-mono text-sm font-semibold">{formatDuration(data.duration_s)}</dd>
        </div>
        <div className="flex flex-col">
          <dt className="text-[11px] text-subtle">
            <Trans>Rank after</Trans>
          </dt>
          <dd className="truncate text-sm font-semibold">{rank ?? "—"}</dd>
        </div>
      </dl>
      {data.analysis_id === null && !isNormalGame(data) ? (
        <p className="text-center">
          <NoAnalysisLabel />
        </p>
      ) : data.analysis_id === null ? (
        <Button
          loading={startAnalysis.isPending}
          onClick={() => {
            startAnalysis.start(matchId);
          }}
        >
          {startAnalysis.isPending ? <Trans>Starting…</Trans> : <Trans>Analyze</Trans>}
        </Button>
      ) : (
        <Button asChild variant={data.analysis_status === "done" ? "default" : "outline"}>
          <Link to="/matches/$matchId" params={{ matchId }} className="no-underline">
            <AnalysisLinkLabel status={data.analysis_status} />
          </Link>
        </Button>
      )}
      <StartAnalysisError error={startAnalysis.error} />
    </div>
  );
};
